import { ref } from 'vue'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { ChatFile } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { useFetchApi } from '@/shared/utils/fetch'
import { isQuotaExceededError } from '@/shared/utils/localStorageException'

import { transformLocalDraftToDraftMessage } from '../lib/transformLocalDraftToDraftMessage'
import { DraftMessageLocal, LocalFile, DraftMessage } from '../model/types'

export const useDraftAPILocal = () => {
  const currentUserImLogin = `${useCurrentUser().currentUser.value?.imLogin}`
  const setItem = (pServed: Room['pServedUser'], item: object) => {
    // key = userID:roomID item = json string
    try {
      localStorage.setItem(`${currentUserImLogin}:${pServed}`, JSON.stringify(item))
    } catch (e: unknown) {
      console.error(e)
      const { t } = useLocalization()
      if (isQuotaExceededError(e)) {
        throw new Error(`${t('common.draftLimitExceeded')}`)
      } else {
        throw new Error(`${t('common.errorSavingDraft')} ` + e)
      }
    }
  }

  const getItemToDraftMessage = (pServed: Room['pServedUser']): Promise<DraftMessage> => {
    // key = userID:roomID
    return new Promise((resolve, reject) => {
      const data = getItem(pServed)
      if (!data) {
        reject(new Error('Draft not found'))
        return
      }
      if (data.files && data.files.length > 0) {
        downloadFile(pServed).then((result) => {
          resolve(transformLocalDraftToDraftMessage(data, result))
        }).catch((err) => reject(err))
      } else {
        resolve(transformLocalDraftToDraftMessage(data))
      }
    })
  }

  const getItem = (pServed: Room['pServedUser']): DraftMessageLocal | null => {
    try {
      const raw = localStorage.getItem(`${currentUserImLogin}:${pServed}`)
      if (!raw) {
        return null
      }
      return JSON.parse(raw) as DraftMessageLocal
    } catch (e: unknown) {
      console.error(e)
      const { t } = useLocalization()
      throw new Error(`${t('common.errorSavingDraft')} ` + e)
    }
  }

  const removeItem = (pServed: Room['pServedUser']) => {
    // key = userID:roomID
    try {
      localStorage.removeItem(`${currentUserImLogin}:${pServed}`)
    } catch (e: unknown) {
      console.error(e)
      const { t } = useLocalization()
      throw new Error(`${t('common.errorSavingDraft')} ` + e)
    }
  }

  const downloadFile = (pServed: Room['pServedUser']): Promise<ChatFile[]> => {
    return new Promise((resolve, reject) => {
      const data = getItem(pServed)
      const fileArrays: ChatFile[] = []
      if (data && data.files) {
        data.files.forEach((item: LocalFile) => {
          useFetchApi().get<BlobPart>('/files/' + item.url, {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/json',
            },
          }).then((response) => {
            if (response.status === 200) {
              fileArrays.push({
                file: new File([response.data], item.name, {
                  type: item.type,
                }),
                name: item.url,
                isError: ref(false),
                isLoaded: ref(true),
                progress: ref(100),
              })
            }
          }).catch((err: unknown) => reject(err))
        })
      }
      resolve(fileArrays)
    })
  }

  return {
    setItem,
    getItem,
    removeItem,
    downloadFile,
    getItemToDraftMessage,
  }
}
